import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Reveal } from "../components/MaskedReveal";
import { IMAGES } from "../lib/images";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

const INTERESTS = ["Wine tastings", "Winery tours", "Social events", "Discovering new wines", "Meeting new people"];
const AGES = ["25–34 years", "35–44 years", "45–54 years", "55–64 years", "64+ years"];
const WINES = ["Red", "White", "Sparkling", "All"];

export default function Join() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    suburb: "",
    age_range: "",
    wine_preference: "",
    interests: [],
    message: "",
    subscribe_newsletter: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const toggleInterest = (i) =>
    setForm((f) => ({
      ...f,
      interests: f.interests.includes(i) ? f.interests.filter((x) => x !== i) : [...f.interests, i],
    }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/members", form);
      setDone(true);
      toast.success("Welcome to the society! 🍷");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="join" data-testid="join" className="relative py-28 md:py-40">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
          {/* Spotlight image */}
          <div className="md:col-span-5 order-2 md:order-1">
            <Reveal className="relative">
              <div className="absolute -inset-6 spotlight" aria-hidden="true" />
              <div className="relative overflow-hidden rounded-[2rem]">
                <img src={IMAGES.pourMoody} alt="Red wine being poured" className="h-[520px] w-full object-cover" />
              </div>
              <div className="mt-6 flex items-center gap-3 text-smoke">
                <span className="h-px w-8 bg-gold" />
                <span className="text-xs uppercase tracking-[0.2em]">No expert knowledge needed</span>
              </div>
            </Reveal>
          </div>

          {/* Form */}
          <div className="md:col-span-7 order-1 md:order-2">
            <Reveal>
              <span className="text-xs uppercase tracking-[0.28em] text-gold">Join the society</span>
              <h2 className="mt-5 font-display leading-[0.95] tracking-tight text-cream text-[clamp(2.4rem,5vw,4.2rem)]">
                Pull up a chair.
              </h2>
              <p className="mt-5 max-w-lg text-smoke leading-relaxed">
                Tell us a little about yourself and we'll send details of our first
                meetup at Gledswood — plus everything that follows.
              </p>
            </Reveal>

            {done ? (
              <Reveal>
                <div data-testid="join-success" className="mt-10 rounded-2xl border border-gold/40 bg-wine-surface p-10 text-center">
                  <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-gold/15">
                    <Check className="h-7 w-7 text-gold" />
                  </div>
                  <h3 className="font-display text-3xl text-cream">You're in. Cheers! 🍷</h3>
                  <p className="mt-3 text-smoke">Keep an eye on your inbox — details of our first gathering are on the way.</p>
                </div>
              </Reveal>
            ) : (
              <form onSubmit={submit} className="mt-10 space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label className="text-smoke">Full name</Label>
                    <Input data-testid="join-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 h-12 bg-wine-surface border-white/15 text-cream" />
                  </div>
                  <div>
                    <Label className="text-smoke">Email</Label>
                    <Input data-testid="join-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 h-12 bg-wine-surface border-white/15 text-cream" />
                  </div>
                  <div>
                    <Label className="text-smoke">Phone</Label>
                    <Input data-testid="join-phone" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 h-12 bg-wine-surface border-white/15 text-cream" />
                  </div>
                  <div>
                    <Label className="text-smoke">Suburb</Label>
                    <Input data-testid="join-suburb" required value={form.suburb} onChange={(e) => setForm({ ...form, suburb: e.target.value })} className="mt-1.5 h-12 bg-wine-surface border-white/15 text-cream" />
                  </div>
                </div>

                <div>
                  <Label className="text-smoke">Your age range</Label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {AGES.map((a) => (
                      <button
                        type="button"
                        key={a}
                        data-testid={`age-${a}`}
                        onClick={() => setForm({ ...form, age_range: a })}
                        className={`rounded-full border px-4 py-2 text-sm transition-all duration-300 ${
                          form.age_range === a ? "border-gold bg-gold text-wine-bg" : "border-white/15 text-smoke hover:border-terracotta"
                        }`}
                      >
                        {a}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="text-smoke">Wine preference</Label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {WINES.map((w) => (
                      <button
                        type="button"
                        key={w}
                        data-testid={`wine-${w}`}
                        onClick={() => setForm({ ...form, wine_preference: w })}
                        className={`rounded-full border px-4 py-2 text-sm transition-all duration-300 ${
                          form.wine_preference === w ? "border-terracotta bg-terracotta/20 text-cream" : "border-white/15 text-smoke hover:border-terracotta"
                        }`}
                      >
                        {w}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="text-smoke">What are you into?</Label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {INTERESTS.map((i) => {
                      const on = form.interests.includes(i);
                      return (
                        <button
                          type="button"
                          key={i}
                          data-testid={`interest-${i}`}
                          onClick={() => toggleInterest(i)}
                          className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm transition-all duration-300 ${
                            on ? "border-terracotta bg-terracotta/20 text-cream" : "border-white/15 text-smoke hover:border-terracotta"
                          }`}
                        >
                          {on && <Check className="h-3.5 w-3.5 text-gold" />}
                          {i}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <Label className="text-smoke">Anything else? (optional)</Label>
                  <Textarea data-testid="join-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5 bg-wine-surface border-white/15 text-cream" />
                </div>

                <label className="flex items-center gap-3 text-sm text-smoke cursor-pointer">
                  <input
                    data-testid="join-newsletter"
                    type="checkbox"
                    checked={form.subscribe_newsletter}
                    onChange={(e) => setForm({ ...form, subscribe_newsletter: e.target.checked })}
                    className="h-4 w-4 accent-gold"
                  />
                  Send me the newsletter with upcoming gatherings.
                </label>

                <button
                  data-testid="join-submit"
                  disabled={submitting}
                  className="w-full rounded-full bg-gold py-4 text-sm font-semibold uppercase tracking-[0.14em] text-wine-bg transition-all duration-300 hover:bg-cream disabled:opacity-60"
                >
                  {submitting ? "Sending…" : "Join the society"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
